import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Formik, FormikProvider } from 'formik';
import { useRecoilValue } from 'recoil';

import extractDataFromColDefs from 'lib/helpers/exportPdf/lib/extractDataFromColDefs';
import { getAgGridColumns, getAgGridData } from 'lib/helpers/exportPdf/lib/getAgGridData';
import { printTable } from 'lib/helpers/exportPdf/printPdf';
import { snakeToTitleCase } from 'lib/helpers/utility';

import { currentPractice } from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Icon from 'components/shared/Icon/Icon';

import exportToCsv from '../../lib/exportToCsv';
import exportToExcel from '../../lib/exportToExcel';
import validationSchema from '../../lib/validationSchema';
import Footer from '../Footer/Footer';

import {
  ActiveFilterList,
  ExportColumnsCheckbox,
  ExportInputTitle,
  ExportTypeCheckbox
} from './components';

const Content = ({ closePopover, useData = () => ({}) }) => {
  const navigate = useNavigate();

  const practice = useRecoilValue(currentPractice);

  const {
    name,
    title,
    filters = {},
    colDefs,
    getData,
    gridApi,
    statusPanelData,
    printExpandedRows,
    queryParams
  } = useData();

  const columns = useMemo(() => {
    let columns = colDefs.filter((item) => item.headerName && item.headerName !== 'actions');

    if (!columns.length && gridApi) {
      columns = getAgGridColumns(gridApi);
    }

    return Object.values(
      columns.reduce((acc, col) => {
        acc[col.field] = col;
        return acc;
      }, {})
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi, colDefs.length]);

  const initialValues = {
    title: snakeToTitleCase(title || name),
    export_type: [],
    export_columns: ['all'],
    selected_columns: []
  };

  const handleExport = async (values, otherProps) => {
    try {
      otherProps.setSubmitting(true);

      let data = [];
      let finalColumns = columns;

      const isExportAllColumns = values.export_columns.includes('all');

      if (!isExportAllColumns) {
        const sortedSelectedColumns = values.selected_columns.sort((a, b) => {
          return (
            columns.findIndex((col) => col.field === a) -
            columns.findIndex((col) => col.field === b)
          );
        });

        finalColumns = sortedSelectedColumns.map((col) => {
          return columns.find((column) => column.field === col);
        });
      }

      if (getData) {
        const params = { ...queryParams, filters, exporting: true };
        const response = await getData(navigate, params);

        data = name ? response?.[name] : response;
      } else if (gridApi) {
        const result = getAgGridData(gridApi, finalColumns);

        data = result.rows;
      }

      if (!data.length) {
        showAlert({ title: 'No data to export!', color: 'warning' });
        return;
      }

      if (values.export_type.includes('csv') || values.export_type.includes('excel')) {
        const columnHeaders = Object.fromEntries(colDefs.map((col) => [col.field, col.headerName]));

        const formattedData = extractDataFromColDefs(data, finalColumns);

        const formattedDataColumns = formattedData.map((obj) => {
          return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [columnHeaders[key] || key, value])
          );
        });

        if (values.export_type.includes('csv')) {
          exportToCsv(formattedDataColumns, values.title);
        }

        if (values.export_type.includes('excel')) {
          exportToExcel(formattedDataColumns, values.title);
        }
      }

      if (values.export_type.includes('pdf')) {
        printTable({
          tableData: data,
          columns: finalColumns,
          statusPanelData,
          printExpandedRows,
          headerConfigs: { practice }
        });
      }

      otherProps.setSubmitting(false);
    } catch (error) {
      console.error('Error while exporting table:', error);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values, otherProps) => handleExport(values, otherProps)}
      validationSchema={validationSchema}>
      {(formValues) => (
        <FormikProvider value={{ ...formValues, columns }}>
          <div className="flex flex-col ">
            <div className="flex justify-between gap-4 bg-white px-4 py-2 shadow-[0px_2px_4px_rgba(27,33,35,0.04)]">
              <span className="text-base font-500 leading-6 text-primary-900">Export Report</span>
              <div
                className="ml-auto flex size-[26px] cursor-pointer select-none items-center justify-center"
                onClick={closePopover}>
                <Icon
                  data-qa="close-square-mono"
                  icon="close-linear"
                  className="cursor-pointer"
                  size={10}
                  onClick={closePopover}
                />
              </div>
            </div>

            <div className="flex max-h-[400px] flex-col gap-4 overflow-y-auto p-4">
              <ExportInputTitle />
              <ExportTypeCheckbox />
              <ExportColumnsCheckbox />
              <ActiveFilterList />
            </div>
          </div>
          <Footer closePopover={closePopover} />
        </FormikProvider>
      )}
    </Formik>
  );
};

export default Content;
